<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { RouterLink } from 'vue-router'

import HeadlineFraunces from '@/design/primitives/HeadlineFraunces.vue'
import PanelOrganic     from '@/design/primitives/PanelOrganic.vue'
import ButtonSeed       from '@/design/primitives/ButtonSeed.vue'
import CoinGlyph        from '@/design/primitives/CoinGlyph.vue'
import IconCheck        from '@/design/icons/IconCheck.vue'

import CargoRow      from '@/pages/Cart/parts/CargoRow.vue'
import MethodStamp   from '@/pages/Cart/parts/MethodStamp.vue'
import SkeletonStamp from '@/pages/Cart/parts/SkeletonStamp.vue'

import { useCartStore } from '@/stores/cartStore'
import { useCurrencyStore } from '@/stores/currencyStore'
import { api }          from '@/api/client'
import { EP }           from '@/api/endpoints'
import CheckoutModal from '@/components/CheckoutModal.vue'

// ─── Cart store ────────────────────────────────────────────────────────────────
const cart = useCartStore()
const currency = useCurrencyStore()
const curSymbol = computed(() => currency.symbol)
const curRate   = computed(() => Number(currency.rate) || 1)
// Cart total in the selected currency (cart.total is in EvoGems, 1 EvoGem = €1).
const payDisplay = computed(() => `${curSymbol.value}${(Number(cart.total) * curRate.value).toFixed(2)}`)

// ─── Auth / balance ────────────────────────────────────────────────────────────
const user    = ref(null)   // null = loading; false = guest; object = authed
const balance = computed(() => user.value?.balance ?? 0)
const authed  = computed(() => !!user.value)

// ─── Payment methods ───────────────────────────────────────────────────────────
// GET /api/payments/checkout-methods returns { methods: [{ id, name, logos }] } —
// one entry per active, checkout-enabled gateway. Rendered as one button each;
// clicking initiates with that gateway's id. "balance" is always prepended.
const loadingMethods  = ref(true)
const externalMethods = ref([])
const filteredExternalMethods = computed(() => externalMethods.value.filter((method) => {
  const allowed = Array.isArray(method.currencies) ? method.currencies : []
  return allowed.length === 0 || allowed.includes(currency.code)
}))

// ─── Selection ─────────────────────────────────────────────────────────────────
const selectedMethod = ref('balance')
watch(() => currency.code, () => {
  if (selectedMethod.value !== 'balance' && !filteredExternalMethods.value.some((method) => method.id === selectedMethod.value)) {
    selectedMethod.value = 'balance'
  }
})

// ─── Submit ────────────────────────────────────────────────────────────────────
const submitting = ref(false)
const errorMsg   = ref('')
const paymentConfig = ref(null)
const paymentConfigError = ref('')

// ─── Success state ─────────────────────────────────────────────────────────────
const successOrderId = ref('')

// ─── Derived ───────────────────────────────────────────────────────────────────
const balanceInsufficient = computed(() =>
  selectedMethod.value === 'balance' && balance.value < cart.total
)
const selectedCurrency = computed(() => (
  currency.available.find((item) => item.code === currency.code) || null
))
const minimumCurrencyAmount = computed(() => {
  const configured = Number(selectedCurrency.value?.minDeposit)
  if (configured > 0) return configured
  const minimumEur = Number(paymentConfig.value?.minDepositEur)
  return minimumEur > 0 && curRate.value > 0 ? minimumEur * curRate.value : null
})
const minimumCartCredits = computed(() => {
  const minimum = Number(minimumCurrencyAmount.value)
  const tokens = Number(paymentConfig.value?.tokensPerEur)
  if (minimum <= 0 || curRate.value <= 0 || tokens <= 0) return null
  return Math.round(((minimum / curRate.value) * tokens) * 100) / 100
})
const cartMeetsMinimum = computed(() => (
  Number(minimumCartCredits.value) > 0
  && Math.round(Number(cart.total) * 100) >= Math.round(minimumCartCredits.value * 100)
))
const minimumCartMessage = computed(() => {
  if (!(Number(minimumCartCredits.value) > 0)) return paymentConfigError.value || 'Minimum cart total is temporarily unavailable.'
  const remaining = Math.max(0, minimumCartCredits.value - Number(cart.total))
  return `Minimum cart total is ${minimumCartCredits.value.toFixed(2)} EvoGems (${curSymbol.value}${minimumCurrencyAmount.value.toFixed(2)}). Add ${remaining.toFixed(2)} more to continue.`
})

// ─── Top-up trigger ───────────────────────────────────────────────────────────
// AppHeader's TopUpModal listens for this event globally.
function openTopUp() {
  window.dispatchEvent(new CustomEvent('open-topup'))
}

// Balance is the only method. If it can't cover the order (e.g. 0 EvoGems),
// selecting it sends the user to top-up instead of selecting an unpayable method.
function onSelectBalance() {
  if (balance.value < cart.total) {
    openTopUp()
    return
  }
  selectedMethod.value = 'balance'
}

function fmt(val) {
  return Number(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

// ─── Load user + payment methods ──────────────────────────────────────────────
async function loadData() {
  loadingMethods.value = true
  try {
    await Promise.all([currency.load(), currency.loadAvailable()])
    const [userRes, methodsRes, configRes] = await Promise.allSettled([
      api.get(EP.authUser),
      api.get(EP.paymentsCheckoutMethods),
      api.get(EP.paymentsConfig)
    ])

    // User — balance lives at data.user.balance
    if (userRes.status === 'fulfilled') {
      user.value = userRes.value.data?.user ?? false
    } else {
      user.value = false
    }

    // Payment methods — server shape: { methods: [{ id, name, logos }] }.
    // Each gateway becomes one selectable button keyed by its numeric id.
    if (methodsRes.status === 'fulfilled') {
      const list = methodsRes.value.data?.methods
      externalMethods.value = Array.isArray(list)
        ? list.map((m) => ({ id: `gw:${m.id}`, gatewayId: m.id, label: m.name, logos: m.logos || [], currencies: m.currencies || [] }))
        : []
    }
    if (configRes.status === 'fulfilled') {
      paymentConfig.value = configRes.value.data
      paymentConfigError.value = ''
    } else {
      paymentConfig.value = null
      paymentConfigError.value = 'Minimum cart configuration is temporarily unavailable.'
    }
  } finally {
    loadingMethods.value = false
  }
}

onMounted(loadData)

// ─── Submit ────────────────────────────────────────────────────────────────────
// NOTE: POST /api/shop/purchase only accepts single-product { productId, quantity }.
// We iterate per item for the balance branch. For external methods we attempt the
// purchase endpoint first (may return redirect), then fall back to /api/payments/initiate.

// ─── Checkout modal (billing details + Terms + delivery target) ─────────────────
const showCheckout = ref(false)
// Any account without a resolved EOS target must provide the exact in-game name.
const needsDeliveryName = computed(() => authed.value && !user.value?.eosId)

function openCheckout() {
  errorMsg.value = ''
  if (!cartMeetsMinimum.value) {
    errorMsg.value = minimumCartMessage.value
    return
  }
  showCheckout.value = true
}

async function confirmCheckout(payload) {
  if (submitting.value) return
  errorMsg.value = ''
  submitting.value = true
  try {
    if (selectedMethod.value === 'balance') {
      await placeBalanceOrder(payload.deliveryName)
    } else {
      await placeExternalOrder(payload.billingInfo, payload.deliveryName)
    }
    showCheckout.value = false
  } catch (err) {
    errorMsg.value = err.message || 'Something went wrong. Please try again.'
  } finally {
    submitting.value = false
  }
}

async function placeBalanceOrder(deliveryName) {
  let lastOrderId = ''

  for (const item of cart.items) {
    const res = await api.post(EP.shopPurchase, {
      productId: item.id,
      quantity: item.qty,
      deliveryName
    })
    if (res.data?.success === false) {
      throw { message: res.data?.message || 'Purchase could not be completed.' }
    }
    lastOrderId = res.data?.orderId || lastOrderId
  }

  cart.clear()
  successOrderId.value = lastOrderId
}

async function placeExternalOrder(billingInfo, deliveryName) {
  const items = cart.items.map((i) => ({ id: i.id, kind: i.kind, qty: i.qty }))
  const method = externalMethods.value.find((m) => m.id === selectedMethod.value)
  const gatewayId = method?.gatewayId
  let redirectUrl = ''

  try {
    const res = await api.post(EP.shopPurchase, {
      items,
      paymentMethod: selectedMethod.value,
      gatewayId,
      deliveryName
    })
    if (res.data?.redirect)    redirectUrl = res.data.redirect
    else if (res.data?.redirectUrl) redirectUrl = res.data.redirectUrl
  } catch (_) {
    // Fall through to initiate
  }

  if (!redirectUrl) {
    const initiateRes = await api.post(EP.paymentsInitiate, {
      customAmount: cart.total,
      billingInfo: billingInfo || {},
      gatewayId,
      deliveryName
    })
    redirectUrl = initiateRes.data?.redirectUrl || initiateRes.data?.redirect || ''
  }

  if (redirectUrl) {
    window.location.href = redirectUrl
  } else {
    throw { message: 'Payment gateway did not return a redirect URL. Please try again.' }
  }
}
</script>

<template>
  <main class="cargo-page">

    <!-- ── Page header band — same shape as Market/Trading ────────────────── -->
    <section class="cargo-page__header u-rise-in">
      <div class="cargo-page__header-inner">
        <p class="cargo-page__kicker u-display u-allcaps">EVOLUXU · CHECKOUT</p>
        <HeadlineFraunces level="h1">Your <em>Cargo</em></HeadlineFraunces>
        <p class="cargo-page__subtitle">
          {{ cart.count > 0
              ? `${cart.count} item${cart.count === 1 ? '' : 's'} ready for delivery.`
              : 'Your cargo hold is empty — wander the Armory to fill it.' }}
        </p>
      </div>
    </section>

    <!-- ── SUCCESS STATE ───────────────────────────────────────────────────── -->
    <template v-if="successOrderId">
      <div class="road-layout road-layout--success u-rise-in">
        <PanelOrganic tone="glass" :grain="true" class="seal">
          <div class="seal__inner">
            <span class="seal__icon-wrap" aria-hidden="true">
              <IconCheck class="seal__icon" />
            </span>
            <p class="seal__heading u-display u-allcaps">Order Logged</p>
            <p class="seal__order-id">
              Order&nbsp;<span class="seal__mono u-mono u-gold">{{ successOrderId }}</span>
            </p>
            <RouterLink to="/profile">
              <ButtonSeed size="lg" variant="primary">View In Profile</ButtonSeed>
            </RouterLink>
          </div>
        </PanelOrganic>
      </div>
    </template>

    <!-- ── EMPTY CARGO HOLD ────────────────────────────────────────────────── -->
    <template v-else-if="cart.items.length === 0">
      <div class="road-layout road-layout--empty u-rise-in">
        <PanelOrganic tone="glass" :padded="true">
          <div class="hold">
            <p class="hold__heading u-display u-allcaps">Empty Cargo Hold</p>
            <p class="hold__sub">Nothing loaded yet.</p>
            <RouterLink to="/market">
              <ButtonSeed variant="ghost" size="md">Browse Store</ButtonSeed>
            </RouterLink>
          </div>
        </PanelOrganic>
      </div>
    </template>

    <!-- ── MANIFEST + SUMMARY ──────────────────────────────────────────────── -->
    <template v-else>
      <div class="road-layout">

        <!-- LEFT — Cargo manifest ───────────────────────────────────────────── -->
        <section class="manifest" aria-label="Cargo manifest">
          <PanelOrganic tone="glass" :padded="false">
            <ol class="manifest__list" role="list">
              <template
                v-for="(item, idx) in cart.items"
                :key="`${item.id}-${item.kind}`"
              >
                <CargoRow
                  :id="item.id"
                  :kind="item.kind"
                  :name="item.name"
                  :desc="item.desc || ''"
                  :price="item.price"
                  :qty="item.qty"
                  :image-url="item.imageUrl || ''"
                />
                <li
                  v-if="idx < cart.items.length - 1"
                  class="manifest__divider"
                  role="separator"
                  aria-hidden="true"
                />
              </template>
            </ol>
          </PanelOrganic>
        </section>

        <!-- RIGHT — Summary panel ───────────────────────────────────────────── -->
        <aside class="summary" aria-label="Order summary">
          <PanelOrganic tone="strong" :grain="true" :padded="true" class="summary__panel">

            <!-- Heading -->
            <h3 class="summary__heading u-display u-allcaps">Order Summary</h3>

            <!-- Total — EvoGems + selected-currency equivalent -->
            <div class="summary__row summary__row--total">
              <span class="summary__total-price">
                <CoinGlyph :size="28" />
                <span class="summary__total-mono u-mono u-gold">{{ fmt(cart.total) }}</span>
              </span>
            </div>
            <p class="summary__total-fiat">
              <!-- Balance pays in internal currency — no fiat conversion shown.
                   External gateways charge fiat, so keep the converted amount there. -->
              <template v-if="selectedMethod === 'balance'">
                You pay <strong class="u-gold">{{ fmt(cart.total) }} EvoGems</strong>
              </template>
              <template v-else>
                You pay <strong class="u-gold">{{ payDisplay }}</strong>
                <span class="summary__total-fiat-unit">/ {{ fmt(cart.total) }} EvoGems</span>
              </template>
            </p>

            <!-- Gold hairline -->
            <div class="gold-rail gold-rail--light" aria-hidden="true" />

            <!-- Payment methods -->
            <div class="summary__methods">
              <p class="summary__methods-label u-display u-allcaps">Payment Method</p>

              <div class="summary__method-list">
                <!-- Skeleton while loading -->
                <template v-if="loadingMethods">
                  <SkeletonStamp />
                </template>

                <template v-else>
                  <!-- Balance is the only payment method (purchases are made with
                       the internal EvoGem currency). When the balance is too low,
                       selecting it routes to top-up — see onSelectBalance. -->
                  <MethodStamp
                    method-id="balance"
                    :label="authed ? 'Use Balance' : 'Sign In To Use Balance'"
                    :sublabel="authed ? `Available: ${fmt(balance)} EvoGems` : ''"
                    :disabled="!authed"
                    :selected="selectedMethod === 'balance'"
                    @select="onSelectBalance"
                  />

                  <!-- External payment gateways — one button per active, checkout-
                       enabled gateway. Lets the buyer pay directly with a chosen
                       method (card etc.) instead of only the internal balance. -->
                  <MethodStamp
                    v-for="m in filteredExternalMethods"
                    :key="m.id"
                    :method-id="m.id"
                    :label="m.label"
                    :logos="m.logos"
                    :selected="selectedMethod === m.id"
                    @select="selectedMethod = m.id"
                  />

                  <!-- Sign-in nudge for guests -->
                  <p v-if="!authed" class="summary__guest-note">
                    <RouterLink to="/register?next=/cart" class="summary__link">Sign in</RouterLink>
                    to use your EvoGem balance.
                  </p>
                </template>
              </div>
            </div>

            <!-- Error -->
            <p v-if="errorMsg" class="summary__error" role="alert">{{ errorMsg }}</p>

            <p v-if="cart.count > 0 && !cartMeetsMinimum" class="summary__error" role="status">
              {{ minimumCartMessage }}
            </p>

            <!-- Insufficient balance warning -->
            <p v-if="balanceInsufficient" class="summary__warn">
              Balance too low —
              <button type="button" class="summary__warn-link" @click="openTopUp">top up</button>
              or choose another method.
            </p>

            <!-- Place order CTA -->
            <ButtonSeed
              size="lg"
              variant="primary"
              type="button"
              :disabled="
                submitting ||
                balanceInsufficient ||
                loadingMethods ||
                !authed ||
                !cartMeetsMinimum
              "
              class="summary__cta"
              @click="openCheckout"
            >
              <template v-if="submitting">
                <span class="summary__spinner" aria-hidden="true" />
                Placing Order…
              </template>
              <template v-else>
                Place Order
              </template>
            </ButtonSeed>

          </PanelOrganic>
        </aside>

      </div>
    </template>

    <!-- Billing details modal — opened from "Place Order" -->
    <CheckoutModal
      :open="showCheckout"
      :total="cart.total"
      :pay-with-balance="selectedMethod === 'balance'"
      :needs-delivery-name="needsDeliveryName"
      :submitting="submitting"
      :error="errorMsg"
      @close="showCheckout = false"
      @confirm="confirmCheckout"
    />

  </main>
</template>

<style scoped>
/* ── Page shell ─────────────────────────────────────────────────────────────── */
.cargo-page {
  min-height: 100dvh;
  padding-bottom: var(--footer-pad);
  /* No bg — global BackgroundField handles the fixed ARK panorama */
}

/* ── Header band — same shape as Market/Trading ─────────────────────────── */
.cargo-page__header {
  min-height: 30vh;
  display: flex;
  align-items: flex-end;
  padding: var(--sp-24) var(--sp-6) var(--sp-12);
  margin-bottom: var(--sp-8);
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(11, 10, 8, 0.6) 70%,
    rgba(11, 10, 8, 0.88) 100%
  );
  border-bottom: 1px solid var(--ash);
}
.cargo-page__header-inner {
  max-width: var(--container-wide);
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--sp-3);
}
.cargo-page__kicker {
  margin: 0;
  font-size: var(--fs-12);
  letter-spacing: 0.22em;
  color: var(--gold);
}
.cargo-page__subtitle {
  margin: 0;
  font-family: var(--font-body);
  font-size: var(--fs-18);
  color: var(--ink-soft);
  line-height: var(--lh-body);
  max-width: 52ch;
}

/* ── Two-column layout ──────────────────────────────────────────────────────── */
.road-layout {
  max-width: var(--container-wide);
  margin: 0 auto;
  padding: 0 var(--sp-6);
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

/* Desktop: side by side */
@media (min-width: 1024px) {
  .road-layout {
    flex-direction: row;
    align-items: flex-start;
    gap: var(--sp-8);
  }
  .manifest { flex: 0 0 60%; }
  .summary  { flex: 0 0 40%; }
}

/* Centered states */
.road-layout--success,
.road-layout--empty {
  max-width: 640px;
  margin-inline: auto;
  flex-direction: column;
  /* Override align-items: flex-start that the desktop @media rule above
     applies — without this the panel collapses to intrinsic width and
     pins to the left edge of the 640px box on viewports ≥1024px. */
  align-items: stretch;
  justify-content: center;
  min-height: 40vh;
}

/* ── Manifest ───────────────────────────────────────────────────────────────── */
.manifest__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.manifest__divider {
  height: 1px;
  background: var(--ash);
  border: none;
  margin: 0 var(--sp-6);
}

/* ── Summary panel ──────────────────────────────────────────────────────────── */
.summary__panel {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
}

/* Sticky on desktop */
@media (min-width: 1024px) {
  .summary {
    position: sticky;
    top: calc(var(--header-h) + var(--sp-4));
  }
}

.summary__heading {
  margin: 0;
  font-size: var(--fs-21);
  color: var(--ink);
  letter-spacing: 0.06em;
}

/* Summary rows */
.summary__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--sp-4);
}

.summary__row--total {
  align-items: center;
  justify-content: center;
}

.summary__label {
  font-size: var(--fs-14);
  color: var(--ink-soft);
}

.summary__count {
  font-size: var(--fs-12);
  color: var(--ink-mute);
  margin-left: var(--sp-1);
}

.summary__price {
  font-size: var(--fs-16);
}

/* Gold hairline divider */
.gold-rail {
  height: 1px;
  background: linear-gradient(90deg,
    transparent 0%,
    var(--gold) 30%,
    var(--gold) 70%,
    transparent 100%);
  opacity: 0.45;
  border: none;
  margin: 0 calc(-1 * var(--sp-8));
}

.gold-rail--light {
  opacity: 0.18;
}

/* Total row */
.summary__total-label {
  font-size: var(--fs-32);
  color: var(--ink);
  line-height: 1;
}

.summary__total-price {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
}

.summary__total-mono {
  font-size: var(--fs-32);
  font-weight: 400;
  line-height: 1;
}

.summary__total-fiat {
  margin: 0;
  text-align: center;
  font-size: var(--fs-13);
  color: var(--ink-soft);
}
.summary__total-fiat-unit {
  margin-left: var(--sp-2);
  color: var(--ink-mute);
}

/* Payment methods */
.summary__methods {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
}

.summary__methods-label {
  margin: 0;
  font-size: var(--fs-12);
  color: var(--ink-soft);
}

.summary__method-list {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
}

.summary__guest-note {
  margin: 0;
  font-size: var(--fs-12);
  color: var(--ink-soft);
  padding-left: var(--sp-1);
}

.summary__link {
  color: var(--gold);
  text-decoration: underline;
  text-decoration-color: rgba(229, 199, 107, 0.35);
  text-underline-offset: 2px;
}
.summary__link:hover { color: var(--gold-deep); }

/* Error + warn */
.summary__error {
  margin: 0;
  padding: var(--sp-3) var(--sp-4);
  border-radius: var(--r-sm);
  border: 1px solid var(--ember);
  background: color-mix(in srgb, var(--ember) 8%, transparent);
  color: var(--ember);
  font-size: var(--fs-14);
  line-height: var(--lh-body);
}

.summary__warn {
  margin: 0;
  font-size: var(--fs-12);
  color: var(--gold-deep);
  text-align: center;
}
.summary__warn-link {
  background: transparent;
  border: 0;
  padding: 0;
  font: inherit;
  color: var(--gold);
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}
.summary__warn-link:hover { filter: brightness(1.15); }

/* CTA */
.summary__cta {
  width: 100%;
  justify-content: center;
}

/* Spinner */
@keyframes cargo-spin {
  to { transform: rotate(360deg); }
}

.summary__spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid color-mix(in srgb, var(--ink-inverse) 30%, transparent);
  border-top-color: var(--ink-inverse);
  border-radius: 50%;
  animation: cargo-spin 0.7s linear infinite;
  flex-shrink: 0;
}

/* ── Success state ──────────────────────────────────────────────────────────── */
.seal__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-4);
  padding: var(--sp-12) var(--sp-6);
  text-align: center;
}

.seal__icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 1px solid rgba(229, 199, 107, 0.4);
  background: rgba(229, 199, 107, 0.08);
  color: var(--gold);
}

.seal__icon {
  width: 44px;
  height: 44px;
}

.seal__heading {
  margin: 0;
  font-family: var(--font-display);
  font-size: var(--fs-32);
  color: var(--ink);
  letter-spacing: 0.04em;
}

.seal__order-id {
  margin: 0;
  font-size: var(--fs-14);
  color: var(--ink-soft);
}

.seal__mono {
  letter-spacing: 0.04em;
}

/* ── Empty state ────────────────────────────────────────────────────────────── */
.hold {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-4);
  padding: var(--sp-12) var(--sp-6);
  text-align: center;
}

.hold__heading {
  margin: 0;
  font-family: var(--font-display);
  font-size: clamp(var(--fs-26), 4vw, var(--fs-44));
  color: var(--ink);
  letter-spacing: 0.04em;
}

.hold__sub {
  margin: 0;
  font-size: var(--fs-16);
  color: var(--ink-soft);
}

/* ── Mobile ─────────────────────────────────────────────────────────────────── */
@media (max-width: 640px) {
  .band {
    padding: 0 var(--sp-4);
    flex-direction: column;
    align-items: flex-start;
    gap: var(--sp-2);
    margin-bottom: var(--sp-6);
  }

  .road-layout {
    padding: 0 var(--sp-4);
    gap: var(--sp-4);
  }

  .gold-rail {
    margin: 0 calc(-1 * var(--sp-6));
  }
}
</style>
